import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar-plus.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ContentSpinner from '@/components/ContentSpinner';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerVacationBalanceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { VacationType } from '@/utils/enums';

import { BoxHeadingBrake, BoxHeadingRow, EditButton } from '../..';
import FreelancerEditVacationModal from './FreelancerEditVacationModal';
import FreelancerExtraDaysModal from './FreelancerExtraDaysModal';
import FreelancerVacationMoreModal from './FreelancerVacationMoreModal';
import FreelancerVacationRequestModal from './FreelancerVacationRequestModal';
import FreelancersVacationTable from './FreelancersVacationTable';

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  padding: 24px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  display: flex;
  margin-bottom: 56px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const VacationInfoRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const VacationInfoBox = styled.div`
  display: flex;
  flex: 1;
  padding: 24px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.grayscaleSuperExtraLight};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  flex-direction: column;
  gap: 8px;
`;

const Heading = styled(Text)`
  width: 100%;
  margin-bottom: 16px;
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AcionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.span<{ $color: 'purple' | 'green' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ theme, $color }) =>
    $color === 'purple' ? theme.colors.purple : theme.colors.success};
`;

const DaysCount = styled(Text)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExtraDays = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ExtraDay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 16px;
  color: ${({ theme }) => theme.colors.error};
`;

const FreelancerVacation = () => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showExtraDaysModal, setShowExtraDaystModal] = useState(false);

  const userCanMangeData = useHasPermission('manage freelancers');

  const { id } = useParams();

  const { isLoading, data: balance } =
    useQuery<FreelancerVacationBalanceResource>(
      ['freelancer_vacation_balance', { id }],
      () =>
        api.get<never, FreelancerVacationBalanceResource>(
          `/v1/payments/freelancers/holidays/balance/${id}`,
        ),
      {
        staleTime: 60 * 1000,
        retry: 2,
      },
    );

  const onDaysDelete = async (adjustmentId: number) => {
    const confirmed = await alerts.confirm(
      'Are you sure you want to delete extra days?',
    );
    if (!confirmed) {
      return;
    }
    try {
      await api.delete(
        `/v1/payments/freelancers/holidays/adjustments/${adjustmentId}`,
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
        exact: false,
      });
      alerts.success('Extra days deleted successfully');
    } catch (e) {
      alerts.error('Failed to delete extra days');
    }
  };

  if (isLoading || !balance) {
    return <ContentSpinner />;
  }

  return (
    <>
      <BoxHeadingRow>
        <Text $color="gray" $type="smallHeadline">
          VACATION
        </Text>
        <BoxHeadingBrake />
        {userCanMangeData && (
          <AcionButtons>
            <EditButton
              small
              color="secondary"
              onClick={() => setShowMoreModal(true)}
            >
              More
            </EditButton>
          </AcionButtons>
        )}
      </BoxHeadingRow>
      <Box>
        <Heading $type="button">Vacation Overview</Heading>
        <VacationInfoRow>
          <VacationInfoBox>
            <HeadingRow>
              <Text $type="captionSemibold">Days Earned</Text> <CalendarIcon />
            </HeadingRow>
            <DaysCount $type="heading1">
              <Dot $color="purple" /> {balance.allocatedDays}
            </DaysCount>
            <ExtraDays>
              {!!balance.adjustments.length &&
                balance.adjustments.map((adjustment) => (
                  <ExtraDay key={adjustment.id}>
                    <Text $type="smallBold" $color="gray">
                      {adjustment.daysAdded} extra{' '}
                      {adjustment.daysAdded === 1 ? 'day' : 'days'} added by{' '}
                      {adjustment.adjustedBy.name} ·{' '}
                      {DateTime.fromISO(adjustment.createdAt).toFormat(
                        'yyyy/LL/dd',
                      )}{' '}
                      ({adjustment.reason})
                    </Text>
                    <DeleteButton onClick={() => onDaysDelete(adjustment.id)}>
                      <TrashIcon />
                    </DeleteButton>
                  </ExtraDay>
                ))}
            </ExtraDays>
          </VacationInfoBox>
          <VacationInfoBox>
            <HeadingRow>
              <Text $type="captionSemibold">Available Days</Text>{' '}
              <CalendarIcon />
            </HeadingRow>
            <DaysCount $type="heading1">
              <Dot $color="green" /> {balance.availableDays}
            </DaysCount>
            {balance.vacationType === VacationType.Instant && (
              <Text $type="smallBold" $color="gray">
                Vacation days (
                <Text as="span" $type="smallBold" $color="black">
                  {isLoading ? '...' : balance?.allocatedDaysAfterReset ?? 0}
                </Text>
                ) will reset at {balance?.resetDate ?? '-'}.
              </Text>
            )}
          </VacationInfoBox>
        </VacationInfoRow>
      </Box>
      <FreelancersVacationTable />
      <AnimatePresence>
        {(showRequestModal ||
          showEditModal ||
          showExtraDaysModal ||
          showMoreModal) &&
          userCanMangeData && (
            <Modal>
              {showRequestModal && (
                <FreelancerVacationRequestModal
                  onClose={() => setShowRequestModal(false)}
                />
              )}
              {showExtraDaysModal && (
                <FreelancerExtraDaysModal
                  onClose={() => setShowExtraDaystModal(false)}
                />
              )}
              {showEditModal && (
                <FreelancerEditVacationModal
                  balance={balance}
                  onClose={() => setShowEditModal(false)}
                />
              )}
              {showMoreModal && (
                <FreelancerVacationMoreModal
                  onOpenVacationSettings={() => setShowEditModal(true)}
                  onAddVacationRequest={() => setShowRequestModal(true)}
                  onAddVacationDays={() => setShowExtraDaystModal(true)}
                  onClose={() => setShowMoreModal(false)}
                />
              )}
            </Modal>
          )}
      </AnimatePresence>
    </>
  );
};

export default FreelancerVacation;
